<div
  class="subscribe-dialog common-style form internal-mode"
  [attr.data-theme]="data?.isDarkTheme ? 'dark' : 'light'"
  [class.dark-theme]="data?.isDarkTheme"
  [class.light-theme]="!data?.isDarkTheme"
  [style.--status-primary]="data?.primaryColor || null">
  <div class="dialog-header d-flex align-items-center justify-content-between">
    @if (currentStep === 'channel') {
      <h3 class="dialog-title mb-0">Choose Notification Channel</h3>
    }
    @if (currentStep === 'service-selection') {
      <h3 class="dialog-title mb-0">Notification Preferences</h3>
    }
    <button mat-icon-button class="close-btn" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (currentStep === 'channel') {
    <div class="internal-channel-content">
      <p class="internal-channel-note">
        Choose the channels you want to receive status updates on.
      </p>

      @if (isInternalProfileLoading) {
        <output class="internal-channel-loading" aria-live="polite">
          <mat-progress-spinner [diameter]="24" mode="indeterminate"></mat-progress-spinner>
          <span>Loading your contact methods...</span>
        </output>
      }

      @if (!isInternalProfileLoading && internalChannelError) {
        <div class="internal-channel-alert" role="alert" aria-live="assertive">
          <mat-icon>error_outline</mat-icon>
          <span>{{ internalChannelError }}</span>
        </div>
      }

      @if (!isInternalProfileLoading && !internalChannelError) {
        <div class="internal-channel-list">
          @for (channel of availableChannels; track trackByChannelId(i, channel); let i = $index) {
            <mat-checkbox
              color="primary"
              class="internal-channel-checkbox"
              [checked]="isInternalChannelChecked(channel.id)"
              (change)="onInternalChannelToggled(channel.id, $event.checked)">
              <span class="internal-channel-label">{{ channel.label }}</span>
            </mat-checkbox>
          }
        </div>
      }

      @if (internalChannelSelectionMessage) {
        <small class="text-danger d-block mt-2">{{ internalChannelSelectionMessage }}</small>
      }
      @if (isLimitReached) {
        <small class="text-danger d-block mt-2">{{ limitMessage }}</small>
      }

      <div class="dialog-actions internal-channel-actions d-flex justify-content-end gap-2">
        <button mat-stroked-button class="action-btn neutral-btn" (click)="close()">Cancel</button>
        <button mat-flat-button color="primary" class="action-btn primary-btn" [disabled]="isInternalContinueDisabled" (click)="onContinue()">
          Continue
        </button>
      </div>
    </div>
  }

  @if (currentStep === 'service-selection') {
    <div class="service-selection-content internal-manage-content">
      <section class="channel-summary-card">
        <div class="channel-summary-header">
          <div class="channel-summary-copy">
            <span class="summary-eyebrow">Notification channels</span>
            <h4 class="summary-title">Using your profile contact methods</h4>
            <p class="summary-note">
              We will notify you through the channels selected below.
            </p>
          </div>
          @if (!isSessionManageMode) {
            <button
              mat-button
              type="button"
              class="change-selection-btn"
              [disabled]="isSubmitting"
              (click)="onBackToChannels()">
              Change
            </button>
          }
        </div>
        <div class="channel-pill-list">
          @for (channelLabel of selectedChannelLabels; track channelLabel) {
            <span class="channel-pill">{{ channelLabel }}</span>
          }
        </div>
        @if (isSessionManageMode) {
          <div class="channel-summary-meta">
            Applied during this popup session.
          </div>
        }
      </section>

      <section class="internal-preferences-card">
        <div class="mode-card">
          <mat-radio-group
            class="mode-options"
            [value]="currentMode"
            [disabled]="modeLocked"
            (change)="onModeChanged($event.value)">
            <mat-radio-button class="mode-option" value="all">
              <span class="mode-option-label">All services</span>
            </mat-radio-button>
            <mat-radio-button class="mode-option" value="selected">
              <span class="mode-option-label">Selected services only</span>
            </mat-radio-button>
          </mat-radio-group>

          <div class="mode-note">
            {{ modeNote }}
          </div>
        </div>

        <div class="service-list-wrapper services-card" [class.disabled]="servicesLocked">
          <div class="service-search-row">
            <div class="service-search-wrap">
              <input
                type="text"
                class="service-search-input"
                [value]="serviceSearchQuery"
                placeholder="Search services..."
                [disabled]="servicesLocked"
                (input)="onServiceSearchInput($event)" />
              @if (!hasActiveServiceSearch) {
                <mat-icon class="service-search-icon" aria-hidden="true">search</mat-icon>
              } @else {
                <button
                  type="button"
                  class="service-search-clear"
                  aria-label="Clear search"
                  [disabled]="servicesLocked"
                  (click)="clearServiceSearch()">
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              }
            </div>
          </div>
          <div class="service-list-panel">
            @if (servicesLoading) {
              <div class="d-flex justify-content-center py-3">
                <mat-progress-spinner [diameter]="26" mode="indeterminate"></mat-progress-spinner>
              </div>
            }
            @if (!servicesLoading && servicesLoadError) {
              <div class="empty-services" role="alert" aria-live="assertive">
                {{ servicesLoadError }}
              </div>
            }
            @if (!servicesLoading && !servicesLoadError) {
              @if (filteredServiceGroups.length > 0) {
                @for (group of filteredServiceGroups; track trackByServiceGroupId($index, group)) {
                  <div class="group-block">
                    <div class="group-header">
                      <mat-checkbox
                        color="primary"
                        class="group-select-checkbox"
                        [attr.aria-label]="'Select ' + group.name"
                        [checked]="isServiceGroupSelected(group)"
                        [indeterminate]="isServiceGroupIndeterminate(group)"
                        [disabled]="servicesLocked"
                        (change)="toggleServiceGroup(group, $event.checked)">
                        <div class="group-header-text">
                          <span class="group-title">{{ group.name }}</span>
                          <span class="group-count">{{ group.services.length }} services</span>
                        </div>
                      </mat-checkbox>
                      <button
                        type="button"
                        class="group-expand-btn"
                        [disabled]="servicesLocked"
                        (click)="toggleGroupExpansion(group)">
                        <mat-icon class="group-toggle-icon">{{ group.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                      </button>
                    </div>
                    @if (group.expanded) {
                      <div class="group-children">
                        @for (service of group.services; track trackByServiceSelectionId($index, service)) {
                          <div class="service-row group-service-row">
                            <mat-checkbox
                              color="primary"
                              [checked]="isServiceSelected(service)"
                              [disabled]="servicesLocked"
                              (change)="toggleService(service, $event.checked)">
                              <div class="service-row-content">
                                <span class="service-label">{{ service.name }}</span>
                                @if (service.description) {
                                  <span class="service-description">{{ service.description }}</span>
                                }
                              </div>
                            </mat-checkbox>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              }
              @for (service of filteredUngroupedServiceOptions; track trackByServiceSelectionId($index, service)) {
                <div class="service-row ungrouped-row">
                  <mat-checkbox
                    color="primary"
                    [checked]="isServiceSelected(service)"
                    [disabled]="servicesLocked"
                    (change)="toggleService(service, $event.checked)">
                    <div class="service-row-content">
                      <span class="service-label">{{ service.name }}</span>
                      @if (service.description) {
                        <span class="service-description">{{ service.description }}</span>
                      }
                    </div>
                  </mat-checkbox>
                </div>
              }
              @if (hasActiveServiceSearch && !hasServiceSearchResults) {
                <div class="empty-services">
                  No matching services found.
                </div>
              }
              @if (!hasActiveServiceSearch && serviceGroups.length === 0 && ungroupedServiceOptions.length === 0) {
                <div class="empty-services">
                  No services available.
                </div>
              }
            }
          </div>
        </div>
      </section>

      @if (serviceTreeTouched && !subscribeAllServices && selectedServiceIds.length === 0) {
        <div class="inline-alert inline-alert-error" role="alert" aria-live="assertive">
          <mat-icon>error_outline</mat-icon>
          <span>Select at least one service.</span>
        </div>
      }
      @if (isLimitReached) {
        <div class="inline-alert inline-alert-error" role="alert" aria-live="assertive">
          <mat-icon>error_outline</mat-icon>
          <span>{{ limitMessage }}</span>
        </div>
      }
      @if (sessionSuccessMessage) {
        <div class="inline-alert inline-alert-success" role="status" aria-live="polite">
          <mat-icon>check_circle</mat-icon>
          <span>{{ sessionSuccessMessage }}</span>
        </div>
      }
      @if (submitError) {
        <div
          class="inline-alert"
          [class.inline-alert-error]="submitNoticeType === 'error'"
          [class.inline-alert-info]="submitNoticeType === 'info'"
          role="alert"
          aria-live="assertive">
          <mat-icon>{{ submitNoticeType === 'error' ? 'error_outline' : 'info' }}</mat-icon>
          <span>{{ submitError }}</span>
        </div>
      }

      @if (!showCloseOnlyAfterDuplicate && !isSessionManageMode) {
        <div class="actions-row">
          <button mat-stroked-button class="action-btn neutral-btn" [disabled]="isSubmitting" (click)="onBackToChannels()">Back</button>
          <button mat-flat-button color="primary" class="action-btn primary-btn" [disabled]="isSubmitting || isLimitReached || servicesLoading || !!servicesLoadError" (click)="onSubscribe()">
            <span class="btn-label">Subscribe</span>
          </button>
        </div>
      }
      @if (!showCloseOnlyAfterDuplicate && isSessionManageMode) {
        <div class="actions-row">
          <button mat-flat-button color="primary" class="action-btn primary-btn" (click)="onDone()">
            Done
          </button>
        </div>
      }
      @if (showCloseOnlyAfterDuplicate) {
        <div class="actions-row">
          <button mat-flat-button color="primary" class="action-btn primary-btn" (click)="close()">
            Close
          </button>
        </div>
      }
    </div>
  }
</div>
